import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, Check } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { NAV_KEYS, WHATSAPP_LINK } from "../data/constants";

export const Header = () => {
  const { t, lang, setLang, languages } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-[60] transition-all duration-500 ${
        scrolled ? "bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/10 py-3" : "bg-transparent py-5"
      }`}
      data-testid="site-header"
    >
      <div className="px-6 md:px-12 lg:px-20 flex items-center justify-between gap-6">
        {/* Logo */}
        <button onClick={() => go("home")} className="flex flex-col items-start leading-none" data-testid="logo-button">
          <span className="font-serif text-xl sm:text-2xl tracking-[0.18em] text-[#FFFFF0]">PHANTOM</span>
          <span className="text-[0.6rem] sm:text-[0.65rem] tracking-[0.42em] text-[#D4AF37] mt-0.5">RESIDENCE</span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-7">
          {NAV_KEYS.map((key) => (
            <button
              key={key}
              onClick={() => go(key)}
              className="text-xs uppercase tracking-[0.16em] text-[#FFFFF0]/70 hover:text-[#D4AF37] transition-colors duration-300"
              data-testid={`nav-${key}`}
            >
              {t.nav[key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Language switcher */}
          <div className="relative">
            <button
              onClick={() => setLangOpen((v) => !v)}
              onBlur={() => setTimeout(() => setLangOpen(false), 150)}
              className="flex items-center gap-2 px-3 py-2 border border-white/15 text-[#FFFFF0]/80 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              data-testid="lang-switcher"
            >
              <Globe size={15} strokeWidth={1.5} />
              <span className="text-xs tracking-[0.12em]">
                {languages.find((l) => l.code === lang)?.short}
              </span>
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute end-0 mt-2 w-44 bg-[#141414] border border-[#2E2E2E] py-2 z-50"
                >
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onMouseDown={() => { setLang(l.code); setLangOpen(false); }}
                      className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors hover:bg-white/5 ${
                        lang === l.code ? "text-[#D4AF37]" : "text-[#FFFFF0]/75"
                      }`}
                      data-testid={`lang-option-${l.code}`}
                    >
                      <span>{l.label}</span>
                      {lang === l.code && <Check size={14} strokeWidth={1.6} />}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* CTA */}
          <button
            onClick={() => go("contact")}
            className="hidden lg:inline-flex items-center px-5 py-2.5 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.16em] hover:bg-[#B5952F] transition-colors"
            data-testid="header-cta"
          >
            {t.nav.cta}
          </button>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMenuOpen(true)}
            className="xl:hidden h-10 w-10 flex items-center justify-center text-[#FFFFF0]"
            data-testid="mobile-menu-open"
            aria-label="Open menu"
          >
            <Menu size={24} strokeWidth={1.4} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#0A0A0A] flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-5 flex items-center justify-between border-b border-white/10">
              <span className="font-serif text-xl tracking-[0.18em] text-[#FFFFF0]">PHANTOM RESIDENCE</span>
              <button onClick={() => setMenuOpen(false)} className="h-10 w-10 flex items-center justify-center text-[#FFFFF0]" data-testid="mobile-menu-close" aria-label="Close menu">
                <X size={24} strokeWidth={1.4} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 py-8 flex flex-col gap-1">
              {NAV_KEYS.map((key, i) => (
                <motion.button
                  key={key}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => go(key)}
                  className="text-start py-3 text-2xl font-serif font-light text-[#FFFFF0]/85 hover:text-[#D4AF37] transition-colors border-b border-white/5"
                  data-testid={`mobile-nav-${key}`}
                >
                  {t.nav[key]}
                </motion.button>
              ))}
            </nav>
            <div className="px-6 py-6 border-t border-white/10">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="block w-full text-center py-3 bg-[#D4AF37] text-black text-sm uppercase tracking-[0.16em]" data-testid="mobile-whatsapp">
                WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
